import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { CheckCircle2, XCircle, ShieldCheck, ShieldAlert, ExternalLink, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtFecha, fmtFechaHora } from "@/lib/fechas";
import { fmtNumeroDeca } from "@/lib/textos";

/**
 * Página pública (SIN auth) de verificación de autenticidad de un DeCA.
 * URL: /verify/:token
 * Llama a /api/public/verify/{token} y muestra el resultado.
 */
export default function PublicVerifyPage() {
  const { token: tokenParam } = useParams();
  const [search] = useSearchParams();
  const token = tokenParam || search.get("token");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!token) { setErr("Falta el token"); setLoading(false); return; }
      try {
        const { data } = await axios.get(`${API}/public/verify/${token}`);
        setData(data);
      } catch (e) {
        setErr(e.response?.data?.detail || e.message);
      } finally { setLoading(false); }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-md bg-blue-700 flex items-center justify-center"><Truck className="h-5 w-5 text-white"/></div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Verificación pública</div>
            <div className="text-xl font-bold" style={{ fontFamily: "Chivo" }}>DeCA Web</div>
          </div>
        </div>

        {loading ? (
          <Card className="p-6 text-center text-slate-500">Comprobando autenticidad…</Card>
        ) : err ? (
          <Card className="p-6 border-red-200 bg-red-50">
            <div className="flex items-center gap-2 text-red-800"><XCircle className="h-5 w-5"/> <span className="font-semibold">No se pudo verificar</span></div>
            <div className="text-sm text-red-700 mt-1">{err}</div>
          </Card>
        ) : (
          <Card className="p-6">
            <div className={`flex items-center gap-3 rounded-md p-3 border ${
              data.revocado ? "border-red-300 bg-red-50 text-red-900"
              : data.valid ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : "border-red-200 bg-red-50 text-red-900"
            }`} data-testid="verify-status">
              {data.revocado ? <XCircle className="h-6 w-6"/> : data.valid ? <CheckCircle2 className="h-6 w-6"/> : <ShieldAlert className="h-6 w-6"/>}
              <div>
                <div className="text-lg font-semibold">
                  {data.revocado ? "DeCA ANULADO" : data.valid ? "Documento auténtico" : "Autenticidad no verificable"}
                </div>
                <div className="text-sm">
                  {data.revocado
                    ? "Este DeCA fue revocado por el emisor y ya no es válido para inspección."
                    : data.valid
                      ? "El hash del PDF coincide con la firma del servidor. No ha sido alterado."
                      : `Motivo: ${data.reason || "desconocido"}.`}
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <KV label={"N" + String.fromCharCode(0xba) + " DeCA"}>{fmtNumeroDeca(data)}</KV>
              <KV label="Versión">{data.version} · <span className="uppercase">{data.estado}</span></KV>
              <KV label="Cargador">{data.cargador?.razon_social}<div className="text-[11px] text-slate-500 font-mono">NIF {data.cargador?.nif}</div></KV>
              <KV label="Transportista">{data.transportista?.razon_social}<div className="text-[11px] text-slate-500 font-mono">NIF {data.transportista?.nif}</div></KV>
              <KV label="Fecha del transporte">{fmtFecha(data.fecha_transporte)}</KV>
              {data.fecha_descarga ? <KV label="Fecha de descarga">{fmtFecha(data.fecha_descarga)}</KV> : null}
              <KV label="Envíos">{data.num_envios}</KV>
              <KV label="Firmado">{fmtFechaHora(data.signed_at)}</KV>
              <KV label="Tamaño PDF">{(data.pdf_size_bytes/1024).toFixed(1)} KB</KV>
            </div>

            <div className="mt-4 border-t border-slate-200 pt-4">
              <div className="label-tag mb-1 flex items-center gap-1"><ShieldCheck className="h-3 w-3"/> Sello criptográfico</div>
              {data.hash_sha256 || data.signature_hmac_sha256 ? (<>
                <div className="text-[11px] font-mono break-all text-slate-700">
                  sha256(pdf) = {data.hash_sha256}
                </div>
                <div className="text-[11px] font-mono break-all text-slate-700 mt-1">
                  hmac       = {data.signature_hmac_sha256}
                </div>
              </>) : (
                <div className="text-[11px] text-slate-500">Este DeCA se emitió antes de la firma HMAC.</div>
              )}
              {!data.valid && data.reason === "hash_mismatch" ? (
                <div className="text-[11px] font-mono break-all text-red-700 mt-1">
                  live_sha256 = {data.live_hash_sha256}
                </div>
              ) : null}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {data.revocado || data.download_activa === false ? (
                <Button disabled variant="outline" data-testid="btn-download-verify-disabled">
                  <ExternalLink className="h-4 w-4 mr-2"/> {data.revocado ? "Descarga desactivada (anulado)" : "Descarga caducada"}
                </Button>
              ) : (
                <a href={data.download_url} target="_blank" rel="noreferrer">
                  <Button className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="btn-download-verify">
                    <ExternalLink className="h-4 w-4 mr-2"/> Descargar PDF
                  </Button>
                </a>
              )}
            </div>

            {data.download_disabled_at ? (
              <div className="mt-3 text-xs text-amber-800 border border-amber-200 bg-amber-50 rounded p-2">
                {data.download_activa !== false
                  ? `La descarga se desactiva el ${fmtFechaHora(data.download_disabled_at)} (${data.download_ttl_days || 7} d\u00edas desde la fecha de transporte, fin del d\u00eda).`
                  : `Descarga desactivada desde ${fmtFechaHora(data.download_disabled_at)}.`}
              </div>
            ) : null}
          </Card>
        )}

        <div className="mt-4 text-[11px] text-slate-500 text-center">
          Verificación conforme al invariante legal L3 · Resolución de 5/6/2026 (BOE-A-2026-12784)
        </div>
      </div>
    </div>
  );
}

function KV({ label, children }) {
  return (
    <div className="border border-slate-200 rounded-md p-2.5">
      <div className="label-tag">{label}</div>
      <div className="text-slate-900 mt-0.5">{children}</div>
    </div>
  );
}
